import React, { useEffect, useState, useCallback } from "react";
import { motion, useScroll, useSpring, AnimatePresence } from "framer-motion";

/**
 * ScrollProgress — right-rail phase indicator.
 *
 * Tracks active phase via scroll-position of each section (more reliable
 * than IntersectionObserver for tall sections). Also computes the
 * progress *within* the active phase: when the user is past the intro
 * screen and reading event cards, the verbose "Sedang Dibaca + year"
 * label fades out so the focus stays on the card content.
 */
const ScrollProgress = ({ phases }) => {
  const { scrollYProgress } = useScroll();
  const scaleY = useSpring(scrollYProgress, {
    stiffness: 90,
    damping: 30,
    mass: 0.4,
  });

  const [activeId, setActiveId] = useState(phases[0]?.id);
  const [phaseProg, setPhaseProg] = useState(0);

  const recompute = useCallback(() => {
    const anchor = window.innerHeight * 0.45;
    let current = phases[0]?.id;
    let activeRect = null;
    let activeHeight = 0;
    for (const p of phases) {
      const el = document.getElementById(`fase-${p.id}`);
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      if (rect.top <= anchor && rect.bottom > anchor) {
        current = p.id;
        activeRect = rect;
        activeHeight = el.offsetHeight;
      }
    }
    setActiveId((prev) => (prev === current ? prev : current));
    if (activeRect && activeHeight) {
      const scrolledIn = -activeRect.top; // px scrolled past phase top
      const prog = Math.max(0, Math.min(1, scrolledIn / activeHeight));
      setPhaseProg(prog);
    } else {
      setPhaseProg(0);
    }
  }, [phases]);

  useEffect(() => {
    recompute();
    const onScroll = () => recompute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [recompute]);

  const activeIndex = Math.max(
    0,
    phases.findIndex((p) => p.id === activeId)
  );

  // "Focus mode" — past the phase intro screen → hide verbose chrome
  const isFocusMode = phaseProg > 0.16 && phaseProg < 0.92;

  return (
    <aside
      data-testid="scroll-progress-indicator"
      className="fixed right-4 md:right-8 top-1/2 -translate-y-1/2 z-50 hidden sm:flex flex-col items-end gap-4"
      aria-label="Indikator perjalanan"
    >
      {/* Active phase verbose label — fades out in focus mode */}
      <AnimatePresence>
        {!isFocusMode && (
          <motion.div
            key="verbose-label"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col items-end mb-1"
            data-testid="scroll-active-label"
          >
            <span className="text-[10px] uppercase tracking-[0.3em] text-copper">
              Sedang Dibaca
            </span>
            <span
              data-testid="scroll-active-year"
              className="font-serif text-parchment text-2xl md:text-3xl leading-none mt-1 tabular-nums"
            >
              {phases[activeIndex]?.watermark}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Progress rail */}
      <div className="relative h-44 w-[1px] bg-warm-border overflow-hidden">
        <motion.div
          style={{ scaleY, originY: 0 }}
          className="absolute inset-0 bg-copper"
        />
      </div>

      {/* Phase dots */}
      <ul className="flex flex-col gap-3.5">
        {phases.map((p) => {
          const active = activeId === p.id;
          return (
            <li key={p.id} className="flex items-center justify-end gap-3 group">
              <span
                className={`text-[10px] uppercase tracking-[0.25em] transition-all duration-500 ${
                  active && !isFocusMode
                    ? "text-parchment opacity-100 translate-x-0"
                    : "text-warm-muted opacity-0 group-hover:opacity-80 translate-x-2 group-hover:translate-x-0"
                }`}
              >
                {p.watermark}
              </span>
              <a
                href={`#fase-${p.id}`}
                data-testid={`scroll-dot-${p.id}`}
                data-active={active ? "true" : "false"}
                className="relative flex items-center justify-center"
                aria-label={`Lompat ke ${p.title}`}
                aria-current={active ? "true" : undefined}
              >
                <span
                  className={`block transition-all duration-500 ${
                    active
                      ? "h-[2px] w-7 bg-copper"
                      : "h-[1px] w-3.5 bg-warm-muted/60 group-hover:w-5 group-hover:bg-parchment"
                  }`}
                />
                <span className="sr-only">{p.title}</span>
              </a>
            </li>
          );
        })}
      </ul>

      {/* Bottom counter — minimal, always present (anchors orientation) */}
      <div
        data-testid="scroll-active-counter"
        className={`text-[10px] uppercase tracking-[0.3em] tabular-nums transition-opacity duration-500 ${
          isFocusMode ? "opacity-30" : "opacity-100"
        }`}
      >
        <span className="text-parchment">0{activeIndex + 1}</span>
        <span className="text-warm-border">/0{phases.length}</span>
      </div>
    </aside>
  );
};

export default ScrollProgress;
